import React, { useState } from 'react';
import { Play, Trash2, AlertCircle, Loader, CheckCircle, Clock } from 'lucide-react';
import { GlassCard, GlassButton, GlassBadge } from '../ui';
import { cancelProduction, VideoProduction } from '../../services/api';

interface Props {
  production: VideoProduction;
  onDeleted: () => void;
  onClick: () => void;
}

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

interface StatusConfig {
  label: string;
  color: BadgeVariant;
  icon: React.ReactNode;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  draft:             { label: 'Draft',       color: 'default',  icon: <Clock size={14} /> },
  generating_scenes: { label: 'Generating',  color: 'warning',  icon: <Loader size={14} className="spin" /> },
  assembling:        { label: 'Assembling',  color: 'info',     icon: <Loader size={14} className="spin" /> },
  complete:          { label: 'Complete',    color: 'success',  icon: <CheckCircle size={14} /> },
  failed:            { label: 'Failed',      color: 'danger',   icon: <AlertCircle size={14} /> },
};

const ProductionCard: React.FC<Props> = ({ production: p, onDeleted, onClick }) => {
  const [cancelling, setCancelling] = useState(false);
  const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.draft;

  const canCancel = ['draft', 'generating_scenes'].includes(p.status);
  const isComplete = p.status === 'complete';
  const progress = p.total_scenes ? Math.round((p.scenes_complete / p.total_scenes) * 100) : 0;
  const isInProgress = ['generating_scenes', 'assembling'].includes(p.status);

  const handleCancel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Cancel this production?')) return;
    setCancelling(true);
    try {
      await cancelProduction(p.id);
      onDeleted();
    } catch {
      setCancelling(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <GlassCard className="vp-card" onClick={onClick} style={{ cursor: 'pointer' }}>
      <div className="vp-card-top">
        <div className="vp-card-info">
          <span className="vp-card-title">{p.title}</span>
          <span className="vp-card-date">{formatDate(p.created_at)}</span>
        </div>
        <GlassBadge variant={cfg.color}>
          {cfg.icon} {cfg.label}
        </GlassBadge>
      </div>

      {isInProgress && p.status === 'generating_scenes' && (
        <div className="vp-progress-wrap">
          <div className="vp-progress-bar">
            <div className="vp-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="vp-progress-label">{p.scenes_complete} / {p.total_scenes} scenes</span>
        </div>
      )}

      {p.status === 'assembling' && (
        <p className="vp-assembling-text">Assembling final video in cloud…</p>
      )}

      {p.status === 'failed' && p.error && (
        <p className="vp-error-text">{p.error}</p>
      )}

      <div className="vp-card-footer">
        <span className="vp-card-meta">{p.total_scenes} scenes · {p.aspect_ratio}</span>
        <div className="vp-card-actions" onClick={e => e.stopPropagation()}>
          {isComplete && (
            <GlassButton variant="primary" size="sm" icon={<Play size={14} />} onClick={onClick}>
              Play
            </GlassButton>
          )}
          {canCancel && (
            <GlassButton variant="danger" size="sm" icon={<Trash2 size={14} />} onClick={handleCancel} disabled={cancelling}>
              {cancelling ? 'Cancelling…' : 'Cancel'}
            </GlassButton>
          )}
        </div>
      </div>
    </GlassCard>
  );
};

export default ProductionCard;
